"use client";

import { useParams } from "next/navigation";
import { Box } from "@mui/material";
import dynamic from "next/dynamic";
import { PDFProvider } from "@/_contexts/PDFcontext"; 
import { useEffect, useState } from "react";
import toast from "react-hot-toast";


const PdfViewer = dynamic(
  () => import("@/_components/DetailPage/PdfViewer").then((mod) => mod.PdfViewer),
  { ssr: false }
);

export default function PdfPage() {
  const params = useParams();
  let docId = params?.docId;
  if (Array.isArray(docId)) {
    docId = docId[0];
  }

  const [isTagged, setIsTagged] = useState<boolean | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showViewer, setShowViewer] = useState<boolean>(false);

  useEffect(() => {
    const checkTaggedStatus = async () => {
      if (!docId) return;

      try {
        // Fetching metadata and tags in parallel
        const detailsRes = await fetch(`/api/details/${docId}`, { method: "GET" });

        if (!detailsRes.ok) {
          if (detailsRes.status === 404) {
            toast.error(`Document with ID ${docId} not found.`);
            setIsTagged(false);
            return;
          }
          throw new Error("Failed to fetch document details");
        }

        const data = await detailsRes.json();

        if (data) {
          setIsTagged(true); 
          const toastId = toast.loading("Loading document...");
          setShowViewer(true);
          toast.dismiss(toastId);
        } else {
          setIsTagged(false); 
          toast.error("Document not found.");
        }
        
      } catch (err: unknown) {
        console.error("[PdfPage] Failed to check tagging status:", err);
        toast.error("Document not found");
        setIsTagged(false); 
      } finally {
        setLoading(false);
      }
    };

    checkTaggedStatus();
  }, [docId]);

  if (loading) {
    // No UI, toast will appear if needed
    return null;
  }

  // If no docId or the document is untagged, render a message
  if (!docId || isTagged === false) {
    return (
      <Box p={4} textAlign="center">
        <strong>Document not found.</strong>
      </Box>
    );
  }

  // Render PdfViewer only if the document is tagged
  return <PDFProvider>  {showViewer && <PdfViewer docId={docId} />}</PDFProvider>;
}
